import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { CloudflareAiIconUrl } from "./icon.ts";
import { cloudflareMcpTools } from "./mcp-tools.ts";
import type { CloudflareAiIntegration } from "./types.ts";

// Measured on P0's account: a new token, or a changed group, worked on the
// MCP server after 1.4 to 3.6 seconds.
const cloudflarePropagationHint = propagationHint({
  resource: "Cloudflare",
  normalWait: "under 5 seconds",
  maxWait: "30 seconds",
  pollInterval: "2 seconds",
});

const prefixArgs = ["cloudflare-ai", "permission"];

const accessPrompt = `Request access to a Cloudflare account by calling with argv:

  [<account>, <group>..., "--zone", <zone>, "--reason", <reason>]

where:

  - account: The Cloudflare account, as installed in P0
  - group: One or more Cloudflare permission group IDs. Use the list tool to
    enumerate them. Without --zone, request account groups; with --zone,
    request zone groups. Add the groups of the calls you intend to make and
    no others.
  - zone: Optional. A zone name in the account (e.g. 'example.com'), or '*'
    for all zones. For groups on several zones, make one request per zone.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; Cloudflare refuses
a call that your groups do not cover.
`;

export const cloudflareAiMcp: McpServerSpec & { type: "external" } = {
  type: "external",
  label: "Cloudflare",
  iconUrl: CloudflareAiIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: cloudflareMcpTools },
  // The token's groups decide what Cloudflare allows.
  authorization: "upstream",
  url: "https://mcp.cloudflare.com/mcp",
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: cloudflarePropagationHint,
  },
  list: {
    fields: {
      account: {
        description: "Cloudflare account",
        required: [],
      },
      zone: {
        description: "Zone of the account, by name, or * for all zones",
        required: ["account"],
      },
      policies: {
        description:
          "Cloudflare permission group that a token can hold: account groups, or with a zone, zone groups",
        required: ["account"],
      },
    },
    prefixArgs,
  },
};

/** The predefined server, under its key in the MCP server registry. */
export const cloudflareAiMcpServers = { cloudflare: cloudflareAiMcp };

/** Whether {@link cloudflareAiMcp} can be added: only when a Cloudflare
 * account is installed. */
export const cloudflareAiMcpServerAvailability = {
  cloudflare: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "credential",
      (installed["cloudflare-ai"] ?? {}) as CloudflareAiIntegration
    ).length > 0,
};
